import * as React from 'react';
import { cva } from 'class-variance-authority';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { Button } from '../components/ui/button';
import { SvgClose, SvgSuccess, SvgProblem } from '../components/icons';

type ToastType = 'success' | 'error';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration: number;
}

interface ToastContextType {
  addToast: (message: string, type: ToastType, duration?: number) => void;
  clearAll: () => void;
}

const ToastContext = React.createContext<ToastContextType | null>(null);

const toastStyles = cva(
  "flex items-center justify-between bg-white rounded-xl px-3 shadow-md",
  {
    variants: {
      variant: {
        success: "text-green text-shadow-sm",
        error: "text-destructive",
      },
    },
  }
);

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const [parent] = useAutoAnimate();

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const clearAll = () => {
    setToasts([]);
  };

  const addToast = React.useCallback((message: string, type: ToastType, duration = 5000) => {
    const id = crypto.randomUUID();
    const toast: Toast = { id, message, type, duration };

    setToasts(prev => {
      const nextToasts = [...prev, toast];
      return nextToasts.length > 4 ? nextToasts.slice(1) : nextToasts;
    });

    setTimeout(() => removeToast(id), duration);
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, clearAll }}>
      {children}
      <div
        className="flex flex-col gap-2 fixed left-1/2 -translate-x-1/2 top-8 z-[100000] right-4 max-w-lg w-full"
        ref={parent}
      >
        {toasts.map(toast => (
          <div key={toast.id} className={toastStyles({ variant: toast.type })}>
            <div className="flex items-center gap-4">
              {toast.type === "success" && <SvgSuccess className="size-20" />}
              {toast.type === "error" && <SvgProblem className="size-20" />}
              <span className="font-medium text-foreground">{toast.message}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={() => removeToast(toast.id)}>
              <SvgClose className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");

  const toast = {
    success: (msg: string, duration?: number) => ctx.addToast(msg, 'success', duration),
    error: (msg: string, duration?: number) => ctx.addToast(msg, 'error', duration),
    clearAll: ctx.clearAll,
  };

  return { toast };
};
